import { useEffect, useState } from 'react';
import { questApi, shareGame, type DailyQuest, type QuestId } from '@/rewards/questApi';
import { useUserStore } from '@/stores/userStore';
import { useT } from '@/i18n/useI18n';
import type { I18nKey } from '@/i18n/translations';
import { audio } from '@/audio/AudioService';
import { CoinIcon, GemIcon } from '@/components/CurrencyIcon';

const TITLE_KEY: Record<QuestId, I18nKey> = {
  share: 'quests.share',
  play: 'quests.play',
  win: 'quests.win',
};

/**
 * Daily Quests: a Home button + modal with 3 once-a-day quests (share / play 5 /
 * win 10). Progress is server-authoritative; rewards claim once per UTC day.
 */
export function DailyQuests({
  variant = 'banner',
}: {
  variant?: 'banner' | 'nav';
}) {
  const t = useT();
  const setProfile = useUserStore((s) => s.setProfile);
  const [open, setOpen] = useState(false);
  const [quests, setQuests] = useState<DailyQuest[] | null>(null);
  const [busy, setBusy] = useState<QuestId | null>(null);
  const [hasClaimable, setHasClaimable] = useState(false);

  const refresh = () =>
    questApi
      .status()
      .then((r) => {
        setQuests(r.quests);
        setHasClaimable(r.quests.some((q) => q.claimable));
      })
      .catch(() => { /* offline / not logged in */ });

  // Light poll on mount so the Home badge reflects today's claimable quests.
  useEffect(() => { void refresh(); }, []);
  useEffect(() => { if (open) void refresh(); }, [open]);

  const onClaim = async (q: DailyQuest) => {
    if (busy) return;
    setBusy(q.quest);
    try {
      // The share quest first opens the system share sheet (honor system).
      if (q.quest === 'share') {
        const shared = await shareGame(t('quests.share_text'));
        if (!shared) { setBusy(null); return; }
      }
      const r = await questApi.claim(q.quest);
      setProfile({ gems: r.balanceGems, coins: r.balanceCoins });
      audio.play('ui.coin_pickup');
      await refresh();
    } catch {
      audio.play('ui.error');
    }
    setBusy(null);
  };

  return (
    <>
      <button
        onClick={() => { audio.play('ui.tap_primary'); setOpen(true); }}
        className={variant === 'nav' ? 'card' : undefined}
        style={{
          position: 'relative',
          width: '100%',
          minWidth: 0,
          minHeight: variant === 'nav' ? 72 : undefined,
          padding: variant === 'nav' ? '6px 4px 7px' : '10px 14px',
          borderRadius: 12,
          border: variant === 'nav'
            ? '1px solid rgba(244,199,36,0.25)'
            : '1px solid var(--c-yellow)',
          background: variant === 'nav'
            ? 'rgba(0,0,0,0.25)'
            : 'linear-gradient(135deg, rgba(244,199,36,0.16), rgba(244,199,36,0.30))',
          color: 'var(--c-yellow)',
          fontFamily: 'var(--f-display)',
          fontSize: variant === 'nav' ? 9 : 13,
          letterSpacing: variant === 'nav' ? '0.06em' : '0.04em',
          cursor: 'pointer',
          boxShadow: variant === 'nav'
            ? undefined
            : '0 0 14px rgba(244,199,36,0.22)',
          display: variant === 'nav' ? 'flex' : undefined,
          flexDirection: variant === 'nav' ? 'column' : undefined,
          alignItems: variant === 'nav' ? 'center' : undefined,
          justifyContent: variant === 'nav' ? 'center' : undefined,
          gap: variant === 'nav' ? 4 : undefined,
        }}
      >
        {variant === 'nav' ? (
          <>
            <span aria-hidden style={{ height: 40, display: 'grid', placeItems: 'center' }}>
              <img
                className="home-nav-image"
                src="/assets/home-nav/daily-quest.png"
                alt=""
              />
            </span>
            <span
              style={{
                maxWidth: '100%',
                color: 'var(--ink-muted)',
                lineHeight: 1.1,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('quests.button')}
            </span>
          </>
        ) : (
          <>🎯 {t('quests.button')}</>
        )}
        {hasClaimable && (
          <span
            style={{
              position: 'absolute',
              top: variant === 'nav' ? 6 : -5,
              right: variant === 'nav' ? 6 : -5,
              width: variant === 'nav' ? 9 : 12,
              height: variant === 'nav' ? 9 : 12,
              borderRadius: '50%',
              background: 'var(--c-red)',
              boxShadow: variant === 'nav'
                ? '0 0 0 2px rgba(0,0,0,0.35), 0 0 6px var(--c-red)'
                : '0 0 8px var(--c-red)',
            }}
          />
        )}
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(6px)',
            zIndex: 650,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'max(env(safe-area-inset-top,0px),18px) 18px max(env(safe-area-inset-bottom,0px),18px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ width: 360, maxWidth: '100%', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div className="display" style={{ fontSize: 18, color: 'var(--c-yellow)', letterSpacing: '0.04em' }}>
              🎯 {t('quests.title')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-dim)', marginTop: -6 }}>
              {t('quests.reset_note')}
            </div>

            {quests?.map((q) => (
              <QuestRow key={q.quest} q={q} busy={busy === q.quest} onClaim={() => onClaim(q)} t={t} />
            ))}
            {!quests && <div style={{ fontSize: 12, color: 'var(--ink-dim)' }}>…</div>}

            <button className="btn btn-ghost" onClick={() => setOpen(false)} style={{ marginTop: 4 }}>
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function QuestRow({
  q,
  busy,
  onClaim,
  t,
}: {
  q: DailyQuest;
  busy: boolean;
  onClaim: () => void;
  t: ReturnType<typeof useT>;
}) {
  const pct = Math.min(100, Math.round((q.progress / q.target) * 100));
  return (
    <div
      style={{
        border: '1px solid var(--hairline)',
        borderRadius: 12,
        padding: '10px 12px',
        background: 'rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--ink)' }}>{t(TITLE_KEY[q.quest])}</span>
        <span style={{ fontSize: 12, color: 'var(--c-yellow)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          +{q.gems || q.coins} {q.gems ? <GemIcon size={12} /> : <CoinIcon size={12} />}
        </span>
      </div>
      {/* progress bar (share has no counter → show full when claimable) */}
      <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ width: `${q.quest === 'share' ? (q.claimed ? 100 : 0) : pct}%`, height: '100%', background: 'var(--c-cyan)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--ink-dim)' }}>
          {q.quest === 'share' ? '' : `${q.progress}/${q.target}`}
        </span>
        {q.claimed ? (
          <span style={{ fontSize: 11, color: 'var(--c-cyan)' }}>✓ {t('quests.claimed')}</span>
        ) : (
          <button
            onClick={onClaim}
            disabled={busy || (q.quest !== 'share' && !q.claimable)}
            className="btn btn-primary"
            style={{
              fontSize: 11,
              padding: '5px 14px',
              opacity: busy || (q.quest !== 'share' && !q.claimable) ? 0.5 : 1,
            }}
          >
            {busy ? '…' : q.quest === 'share' ? t('quests.share_btn') : t('quests.claim')}
          </button>
        )}
      </div>
    </div>
  );
}
